import React, { useEffect, Suspense, memo } from 'react';
import ReceiptScanner from './components/ReceiptScanner';
import { v4 as uuidv4 } from 'uuid';
import { ExpenseProvider, useExpenseContext } from './contexts/ExpenseContext.tsx';
import ErrorBoundary from './components/ErrorBoundary';
import AuthForm from './components/AuthForm';
import GoogleLoginButton from './components/GoogleLoginButton';
import AppShell from './components/AppShell';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
const MainLayout = React.lazy(() => import('./components/MainLayout'));

// Function to ensure all transactions in localStorage have valid IDs
const fixLocalStorageData = () => {
  try {
    const savedData = localStorage.getItem('expenseTrackerData');
    if (savedData) {
      const data = JSON.parse(savedData);
      
      let needsUpdate = false;
      
      // Ensure all transactions have IDs
      if (data.transactions && Array.isArray(data.transactions)) {
        data.transactions = data.transactions.map(transaction => {
          if (!transaction.id) {
            transaction.id = uuidv4();
            needsUpdate = true;
            console.log("Fixed transaction without ID:", transaction);
          }
          return transaction;
        });
      }
      
      // Save back to localStorage if changes were made
      if (needsUpdate) {
        console.log("Updating localStorage with fixed data");
        localStorage.setItem('expenseTrackerData', JSON.stringify(data));
      }
    }
  } catch (error) {
    console.error("Error fixing localStorage data:", error);
  }
};

const AppContent = memo(() => {
  const { darkMode } = useExpenseContext();
  const { isAuthenticated, login, register, googleLogin, loading } = useAuth();

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  if (!isAuthenticated) {
    return (
      <AppShell>
        <div className="flex flex-col justify-center items-center min-h-[60vh]">
          <AuthForm
            onAuth={async ({ email, password, mode }) =>
              mode === "login"
                ? login({ email, password })
                : register({ email, password })
            }
            loading={loading}
          />
          <GoogleLoginButton onGoogleAuth={googleLogin} />
        </div>
      </AppShell>
    );
  }

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <MainLayout />
      <div className="mt-6">
        <ReceiptScanner />
      </div>
    </Suspense>
  );
});

function App() {
  // Fix localStorage data before rendering the app
  useEffect(() => {
    fixLocalStorageData();
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <ExpenseProvider>
          <SettingsProvider>
            <AppContent />
          </SettingsProvider>
        </ExpenseProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
